import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Discretionary discount codes on subscriptions.
 *
 * Created by an admin — a 5% code for an eshot, a refer-a-friend code per
 * member, a one-off goodwill discount. Codes are never listed publicly: a member
 * can only check a code they already hold, through validate_discount_code().
 */

export interface DiscountCode {
  id: string;
  code: string;
  description: string | null;
  percent_off: number | null;
  amount_off_pence: number | null;
  applies_to_plans: string[];
  max_redemptions: number | null;
  times_redeemed: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  referrer_id: string | null;
  created_at: string;
  stripe_promo_id: string | null;
}

export interface NewDiscountCode {
  code: string;
  description?: string | null;
  percent_off?: number | null;
  amount_off_pence?: number | null;
  applies_to_plans?: string[];
  max_redemptions?: number | null;
  valid_until?: string | null;
}

export interface CodeCheck {
  valid: boolean;
  message?: string;
  code?: string;
  description?: string | null;
  percentOff?: number | null;
  amountOffPence?: number | null;
  stripePromoId?: string | null;
}

export async function fetchDiscountCodes(supabase: SupabaseClient): Promise<DiscountCode[]> {
  const { data, error } = await supabase
    .from('discount_codes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DiscountCode[];
}

export async function createDiscountCode(
  supabase: SupabaseClient,
  input: NewDiscountCode
): Promise<DiscountCode> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('discount_codes')
    .insert({
      ...input,
      code: input.code.trim().toUpperCase(),
      applies_to_plans: input.applies_to_plans ?? [],
      created_by: user?.id ?? null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as DiscountCode;
}

export async function setDiscountCodeActive(
  supabase: SupabaseClient,
  id: string,
  isActive: boolean
): Promise<void> {
  const { error } = await supabase
    .from('discount_codes')
    .update({ is_active: isActive })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteDiscountCode(supabase: SupabaseClient, id: string): Promise<void> {
  const { error } = await supabase.from('discount_codes').delete().eq('id', id);
  if (error) throw error;
}

/** Check a code a member has typed at checkout. */
export async function checkDiscountCode(
  supabase: SupabaseClient,
  code: string,
  plan?: string
): Promise<CodeCheck> {
  const { data, error } = await supabase.rpc('validate_discount_code', {
    code_input: code,
    plan: plan ?? null,
  });
  if (error) return { valid: false, message: 'Could not check that code.' };
  return data as CodeCheck;
}

/** A member's own refer-a-friend code, if one has been issued. */
export async function fetchMyReferralCode(
  supabase: SupabaseClient,
  myId: string
): Promise<DiscountCode | null> {
  const { data, error } = await supabase
    .from('discount_codes')
    .select('*')
    .eq('referrer_id', myId)
    .maybeSingle();
  if (error) return null;
  return (data as DiscountCode) ?? null;
}

export function discountLabel(code: Pick<DiscountCode, 'percent_off' | 'amount_off_pence'>): string {
  if (code.percent_off != null) return `${Number(code.percent_off)}% off`;
  if (code.amount_off_pence != null) return `£${(code.amount_off_pence / 100).toFixed(2)} off`;
  return '—';
}

export function codeStatus(code: DiscountCode): { label: string; tone: string } {
  if (!code.is_active) return { label: 'Switched off', tone: 'bg-muted text-muted-foreground' };
  if (code.valid_until && new Date(code.valid_until) < new Date()) {
    return { label: 'Expired', tone: 'bg-red-100 text-red-700' };
  }
  if (code.max_redemptions != null && code.times_redeemed >= code.max_redemptions) {
    return { label: 'Fully used', tone: 'bg-amber-100 text-amber-800' };
  }
  return { label: 'Live', tone: 'bg-green-100 text-green-700' };
}
